/**
 * Uso dei materiali nelle pareti CERTIFICATE.
 *
 * I materiali impiegati in una stratigrafia certificata non sono cancellabili
 * (il DB lo impedisce già via vincolo di integrità) e la loro identità tecnica
 * non va alterata, perché regge la certificazione dichiarata. Restano invece
 * sempre personalizzabili nel commerciale (prezzo, tempo, sfrido, visibilità).
 *
 * È un calcolo LIVE: eliminata l'ultima parete certificata che lo usa, il
 * materiale si sblocca da solo.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export const useCertifiedUsage = () => {
  const query = useQuery({
    queryKey: ['materials-certified-usage'],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Array<{ material_id: string; certified_wall_count: number }>> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('materials_certified_usage')
        .select('material_id, certified_wall_count');
      if (error) {
        console.error('[useCertifiedUsage] error:', error);
        return [];
      }
      return data ?? [];
    },
  });

  const usageMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of query.data ?? []) m.set(r.material_id, Number(r.certified_wall_count ?? 0));
    return m;
  }, [query.data]);

  return { usageMap, isLoading: query.isLoading };
};

/** Elenco delle pareti certificate che usano un materiale (per il dettaglio del lucchetto). */
export const useCertifiedWallsForMaterial = (materialId: string | null, enabled = true) =>
  useQuery({
    queryKey: ['certified-walls-for-material', materialId],
    enabled: !!materialId && enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Array<{ id: string; name: string }>> => {
      if (!materialId) return [];
      const { data, error } = await supabase
        .from('layers')
        .select('stratigraphy_id, stratigraphies!inner(id, name, is_certified)')
        .or(`material_id.eq.${materialId},screw_material_id.eq.${materialId}`)
        .eq('stratigraphies.is_certified', true);
      if (error) {
        console.error('[useCertifiedWallsForMaterial] error:', error);
        return [];
      }
      const seen = new Map<string, string>();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const row of (data ?? []) as any[]) {
        const s = row.stratigraphies;
        if (s?.id) seen.set(s.id, s.name ?? '(senza nome)');
      }
      return Array.from(seen, ([id, name]) => ({ id, name }));
    },
  });
